import { useState } from 'react';
import { PlusIcon, SendIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  toast,
  type BadgeProps,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/shared/lib/format';
import { todayIso } from '@/shared/lib/formSchema';
import { RegulatoryNotificationDtoStatus, type OffboardingDetailDto } from '@/api/generated/model';
import {
  useAddNotificationMutation,
  useSubmitPendingNotificationsMutation,
  useUpdateNotificationMutation,
} from '../hooks/useOffboarding';
import { StepSection } from './StepSection';

const STATUSES = Object.values(RegulatoryNotificationDtoStatus);

/** Neutral until it has actually happened; red only for a real rejection. */
const STATUS_VARIANT: Record<RegulatoryNotificationDtoStatus, BadgeProps['variant']> = {
  [RegulatoryNotificationDtoStatus.pending]: 'secondary',
  [RegulatoryNotificationDtoStatus.submitted]: 'info',
  [RegulatoryNotificationDtoStatus.acknowledged]: 'success',
  [RegulatoryNotificationDtoStatus.rejected]: 'danger',
};

export interface NotificationsPanelProps {
  offboarding: OffboardingDetailDto;
  readOnly?: boolean;
}

/**
 * Step 3 — the regulatory notification workflow.
 *
 * The banner is not decoration. Nothing here is transmitted to FIFA or any
 * association: moving a row to `submitted` records that a human filed it
 * elsewhere. A compliance screen that implies otherwise is worse than no screen.
 */
export function NotificationsPanel({ offboarding, readOnly = false }: NotificationsPanelProps) {
  const { t } = useTranslation();

  const add = useAddNotificationMutation(offboarding.id);
  const update = useUpdateNotificationMutation(offboarding.id);
  const submitAll = useSubmitPendingNotificationsMutation(offboarding.id);

  const [body, setBody] = useState('');
  const [action, setAction] = useState('');
  const [reference, setReference] = useState('');
  const [dueDate, setDueDate] = useState(todayIso());

  const fail = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : t('offboarding.notifications.failed'));

  const pendingCount = offboarding.notifications.filter(
    (notification) => notification.status === RegulatoryNotificationDtoStatus.pending,
  ).length;

  const canAdd = body.trim().length > 0 && action.trim().length > 0;

  return (
    <StepSection
      step={3}
      title={t('offboarding.notifications.title')}
      extra={
        readOnly ? null : (
          <Button
            variant="outline"
            size="sm"
            disabled={pendingCount === 0}
            loading={submitAll.isPending}
            onClick={() =>
              submitAll.mutate(undefined, {
                onSuccess: () => toast.success(t('offboarding.notifications.allSubmitted')),
                onError: fail,
              })
            }
          >
            <SendIcon />
            {t('offboarding.notifications.submitAll')}
          </Button>
        )
      }
    >
      <div className="grid gap-4">
        <Callout
          variant="info"
          title={t('offboarding.notifications.stubTitle')}
          description={t('offboarding.notifications.stubBody')}
        />

        {offboarding.notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('offboarding.notifications.empty')}</p>
        ) : (
          <ul className="divide-y divide-border/70 rounded-lg border">
            {offboarding.notifications.map((notification) => (
              <li
                key={notification.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div className="grid min-w-0 flex-1 gap-0.5">
                  <span className="text-sm font-medium">
                    {notification.body} — {notification.action}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {[
                      notification.reference
                        ? t('offboarding.notifications.ref', { reference: notification.reference })
                        : null,
                      notification.dueDate
                        ? t('offboarding.notifications.dueOn', {
                            date: formatDate(notification.dueDate),
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_VARIANT[notification.status]}>
                    {t(`offboarding.notificationStatus.${notification.status}`)}
                  </Badge>
                  <Select
                    value={notification.status}
                    disabled={readOnly}
                    onValueChange={(status) =>
                      update.mutate(
                        {
                          notificationId: notification.id,
                          dto: { status: status as RegulatoryNotificationDtoStatus },
                        },
                        {
                          onSuccess: () => toast.success(t('offboarding.notifications.submitted')),
                          onError: fail,
                        },
                      )
                    }
                  >
                    <SelectTrigger
                      size="sm"
                      className="w-40"
                      aria-label={t('offboarding.notifications.title')}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="end">
                      {STATUSES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`offboarding.notificationStatus.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </li>
            ))}
          </ul>
        )}

        {readOnly ? null : (
          <>
            <Separator />
            <form
              className="grid items-end gap-3 md:grid-cols-[minmax(0,6fr)_minmax(0,7fr)_minmax(0,5fr)_minmax(0,4fr)_auto]"
              onSubmit={(event) => {
                event.preventDefault();
                if (!canAdd) return;
                add.mutate(
                  {
                    body: body.trim(),
                    action: action.trim(),
                    reference: reference.trim() || null,
                    dueDate: dueDate || null,
                  },
                  {
                    onSuccess: () => {
                      toast.success(t('offboarding.notifications.added'));
                      setBody('');
                      setAction('');
                      setReference('');
                    },
                    onError: fail,
                  },
                );
              }}
            >
              <Field label={t('offboarding.notifications.body')} htmlFor="notification-body">
                <Input
                  id="notification-body"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder={t('offboarding.notifications.bodyPlaceholder')}
                />
              </Field>
              <Field label={t('offboarding.notifications.action')} htmlFor="notification-action">
                <Input
                  id="notification-action"
                  value={action}
                  onChange={(event) => setAction(event.target.value)}
                  placeholder={t('offboarding.notifications.actionPlaceholder')}
                />
              </Field>
              <Field
                label={t('offboarding.notifications.reference')}
                htmlFor="notification-reference"
              >
                <Input
                  id="notification-reference"
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                />
              </Field>
              <Field label={t('offboarding.notifications.due')} htmlFor="notification-due">
                <Input
                  id="notification-due"
                  type="date"
                  value={dueDate}
                  onChange={(event) => setDueDate(event.target.value)}
                />
              </Field>
              <Button type="submit" variant="outline" loading={add.isPending} disabled={!canAdd}>
                <PlusIcon />
                {t('offboarding.notifications.addNotification')}
              </Button>
            </form>
          </>
        )}
      </div>
    </StepSection>
  );
}
